import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";
import api from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useSubscription();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--pk-bg)" }} data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl p-8 border text-center" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto" />
            <h1 className="font-heading text-xl font-bold text-slate-100 mt-5">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-slate-50 mt-5">You're all set!</h1>
            <p className="text-slate-400 text-sm mt-2">Your subscription is active. New features are unlocked.</p>
            <button data-testid="payment-success-continue" onClick={() => navigate("/billing")}
              className="mt-6 w-full py-3 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors">
              Go to Billing
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="h-14 w-14 text-rose-400 mx-auto" />
            <h1 className="font-heading text-xl font-bold text-slate-100 mt-5">We couldn't confirm your payment</h1>
            <p className="text-slate-400 text-sm mt-2">If you were charged, it may take a moment to reflect. Check Billing shortly.</p>
            <Link to="/billing" className="mt-6 inline-block w-full py-3 rounded-xl font-semibold text-sm border text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              Back to Billing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--pk-bg)" }} data-testid="payment-cancel-page">
      <div className="w-full max-w-md rounded-2xl p-8 border text-center" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
        <Zap className="h-12 w-12 text-amber-400 mx-auto" />
        <h1 className="font-heading text-xl font-bold text-slate-100 mt-5">Checkout canceled</h1>
        <p className="text-slate-400 text-sm mt-2">No charge was made. You can pick a plan whenever you're ready.</p>
        <button onClick={() => navigate("/billing")} className="mt-6 w-full py-3 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors">
          Back to Billing
        </button>
      </div>
    </div>
  );
}
